import { ApolloClient, useApolloClient, useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import {
    createPaginationService,
    type LoadResult,
    type PaginationContext,
    usePaginationService,
} from "../../common/state";
import { AffectedGroupsRepository } from "../api/cache";
import { getCachedAffectedGroups } from "../api/queries/getAffectedGroups";
import {
    type GetCachedAffectedGroupsQuery,
    type GetCachedAffectedGroupsQueryVariables,
} from "../api/queries/getAffectedGroups.gql-generated";

const loadAffectedGroups =
    (client: ApolloClient<any>, lensSourceId: string) =>
    ({ limit }: PaginationContext): Promise<LoadResult> =>
        new AffectedGroupsRepository(client)
            .loadAffectedGroupsToCache({
                lensSourceId,
                limit,
            })
            .then((data) => ({
                isLastPage: !data.getAffectedGroups?.hasMore,
            }));

export function useAffectedGroups(lensSourceId: string) {
    const client = useApolloClient();

    const paginationService = useMemo(
        () => createPaginationService(loadAffectedGroups(client, lensSourceId), { limit: 20 }),
        [client, lensSourceId],
    );

    const { data } = useQuery<GetCachedAffectedGroupsQuery, GetCachedAffectedGroupsQueryVariables>(
        getCachedAffectedGroups,
        {
            fetchPolicy: "cache-only",
        },
    );

    const { firstLoad, reset, ...serviceActions } = usePaginationService(paginationService);

    useEffect(() => {
        firstLoad();
        return () => {
            reset();
            AffectedGroupsRepository.initDefaults(client.cache);
        };
    }, [client, firstLoad, reset]);

    const affectedGroups = useMemo(() => data?.getAffectedGroups?.affectedGroups || [], [data]);

    return { ...serviceActions, affectedGroups };
}
